import { useEffect, useState, type HTMLAttributes, type ReactNode } from 'react';

/**
 * A polite live region that exists before its content (ADR-056).
 *
 * Screen readers announce a change *inside* a live region they already
 * track; a region inserted together with its text is often read as new
 * page content, or not at all. So the region is committed empty, and its
 * children are put in one frame later. Removing the content (a dismiss)
 * empties the region without removing it.
 */
export function DeferredLiveRegion({
  children,
  ...props
}: { readonly children?: ReactNode } & Omit<
  HTMLAttributes<HTMLDivElement>,
  'role' | 'aria-live' | 'children'
>) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    // One frame, so the empty region is in the accessibility tree first; a
    // timer where there is no animation frame (a background tab, a test DOM
    // without one).
    if (typeof globalThis.requestAnimationFrame === 'function') {
      const frame = globalThis.requestAnimationFrame(() => setReady(true));
      return () => globalThis.cancelAnimationFrame(frame);
    }
    const timer = globalThis.setTimeout(() => setReady(true), 0);
    return () => globalThis.clearTimeout(timer);
  }, []);
  return (
    <div role="status" aria-live="polite" {...props}>
      {ready ? children : null}
    </div>
  );
}
